/**
 * SimpleNumbersPnl Component
 * The 4-5 line P&L from Scaling Up seminar:
 * Revenue → COGS → Gross Margin → Direct Labour → Contribution Margin → OpEx → NOP
 */

import { formatCurrency } from '@/utils/formatters';
import { CommentButton } from './CommentButton';
import type { SimpleNumbersPnl as SimpleNumbersData } from '@/types/scalingUp';

interface SimpleNumbersPnlProps {
  data: SimpleNumbersData | null;
  loading: boolean;
  onInsight?: () => void;
}

interface PnlRow {
  id: string;
  label: string;
  amount: number;
  percent: number;
  isBold?: boolean;
  isSeparator?: boolean;
  isNegative?: boolean;
}

function buildRows(d: SimpleNumbersData): PnlRow[] {
  return [
    { id: 'revenue', label: 'Revenue', amount: d.revenue, percent: d.revenuePercent, isBold: true },
    { id: 'cogs', label: 'Less: COGS', amount: d.cogs, percent: d.cogsPercent, isNegative: true },
    { id: 'gross-margin', label: 'GROSS MARGIN', amount: d.grossMargin, percent: d.grossMarginPercent, isBold: true, isSeparator: true },
    { id: 'direct-labour', label: 'Less: Direct Labour', amount: d.directLabour, percent: d.directLabourPercent, isNegative: true },
    { id: 'contribution-margin', label: 'CONTRIBUTION MARGIN', amount: d.contributionMargin, percent: d.contributionMarginPercent, isBold: true, isSeparator: true },
    { id: 'opex', label: 'Less: Operating Expenses', amount: d.operatingExpenses, percent: d.operatingExpensesPercent, isNegative: true },
    { id: 'nop', label: 'NET OPERATING INCOME', amount: d.netOperatingIncome, percent: d.netOperatingIncomePercent, isBold: true, isSeparator: true },
  ];
}

export function SimpleNumbersPnl({ data, loading, onInsight }: SimpleNumbersPnlProps) {
  if (loading && !data) {
    return (
      <div className="scaling-up__card scaling-up__card--wide">
        <h2 className="scaling-up__card-title">Simple Numbers P&amp;L</h2>
        <div className="skeleton" style={{ height: '200px' }} />
      </div>
    );
  }

  if (!data) return null;

  const rows = buildRows(data);

  return (
    <div className="scaling-up__card scaling-up__card--wide">
      <div className="scaling-up__card-header">
        <h2 className="scaling-up__card-title">Simple Numbers P&amp;L</h2>
        <span className="scaling-up__card-period">{data.period.label}</span>
        {onInsight && (
          <button
            className="trend-chart__ai-btn"
            onClick={onInsight}
            title="AI Insights — P&L trend analysis"
            aria-label="AI Insights for Simple Numbers P&L"
          >
            AI
          </button>
        )}
      </div>
      <table className="simple-pnl" aria-label="Simple Numbers Profit and Loss">
        <thead>
          <tr>
            <th className="simple-pnl__label-col">Item</th>
            <th className="simple-pnl__amount-col">Amount</th>
            <th className="simple-pnl__pct-col">% Rev</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.id}
              className={`simple-pnl__row ${row.isBold ? 'simple-pnl__row--bold' : ''} ${row.isSeparator ? 'simple-pnl__row--separator' : ''}`}
            >
              <td className="simple-pnl__label">
                {row.label}
                <CommentButton targetType="pnl-line" targetId={row.id} />
              </td>
              <td className="simple-pnl__amount">
                {row.isNegative ? `(${formatCurrency(row.amount)})` : formatCurrency(row.amount)}
              </td>
              <td className="simple-pnl__pct">{row.percent.toFixed(1)}%</td>
            </tr>
          ))}
        </tbody>
      </table>
      {data.directLabour === 0 && (
        <p className="scaling-up__warning">
          No direct labour accounts identified. Configure account classification
          to separate wages from operating expenses.
        </p>
      )}
    </div>
  );
}
